import {filler} from '@xh/hoist/cmp/layout';
import {hoistCmp, uses} from '@xh/hoist/core';
import {button} from '@xh/hoist/desktop/cmp/button';
import {numberInput, select, textInput} from '@xh/hoist/desktop/cmp/input';
import {panel} from '@xh/hoist/desktop/cmp/panel';
import {toolbar} from '@xh/hoist/desktop/cmp/toolbar';
import {Icon} from '@xh/hoist/icon';
import {dialog} from '@xh/hoist/kit/blueprint';
import {isNil} from 'lodash';
import {AGGREGATORS} from '../GridSpecTypes';
import {configPanelItem, configPanelSwitch, stackedPanel} from './Common';
import {GridSpecConfiguratorModel} from './GridSpecConfiguratorModel';
import {styleRulesPanel} from './StyleRules';

const TYPE_OPTIONS = [
    {value: 'text', label: 'Default'},
    {value: 'number', label: 'Number'},
    {value: 'date', label: 'Date'},
    {value: 'bool', label: 'Yes / No'}
];

const ALIGN_OPTIONS = [
    {value: null, label: 'Default'},
    {value: 'left', label: 'Left'},
    {value: 'center', label: 'Center'},
    {value: 'right', label: 'Right'}
];

const PINNED_OPTIONS = [
    {value: null, label: 'None'},
    {value: 'left', label: 'Left'},
    {value: 'right', label: 'Right'}
];

const NUMBER_STYLE_OPTIONS = [
    {value: 'plain', label: 'Plain'},
    {value: 'thousands', label: 'Thousands'},
    {value: 'millions', label: 'Millions'},
    {value: 'billions', label: 'Billions'},
    {value: 'percent', label: 'Percent'},
    {value: 'price', label: 'Price'},
    {value: 'quantity', label: 'Quantity (k/m)'}
];

const DATE_STYLE_OPTIONS = [
    {value: 'date', label: 'Date'},
    {value: 'dateTime', label: 'Date + Time'},
    {value: 'dateTimeSec', label: 'Date + Time + Seconds'},
    {value: 'time', label: 'Time'}
];

const AGGREGATOR_OPTIONS = [
    {value: null, label: 'None'},
    ...AGGREGATORS.map(a => ({value: a, label: a === 'avg' ? 'Average' : a[0].toUpperCase() + a.slice(1)}))
];

/** Edits a draft of one spec column - saved as ops by `saveColumnDialog()`. */
export const columnDialog = hoistCmp.factory<GridSpecConfiguratorModel>({
    model: uses(GridSpecConfiguratorModel),
    render({model}) {
        const {editField: field, columnDraft: c, specModel} = model;
        if (!field) return null;

        const f = c.format ?? {type: 'text'},
            isNumber = f.type === 'number',
            isDate = f.type === 'date';

        return dialog({
            // Named by the field, not its header - a header can be anything the user typed
            title: `Column: ${specModel.catalogLabel(field)} (${field})`,
            icon: Icon.gear(),
            isOpen: true,
            style: {width: 420},
            canOutsideClickClose: false,
            onClose: () => model.closeColumnDialog(),
            item: panel({
                className: 'gs-column-dialog',
                scrollable: true,
                contentBoxProps: {maxHeight: '70vh'},
                items: [
                    stackedPanel({
                        title: 'Column',
                        items: [
                            configPanelItem({
                                label: 'Header',
                                item: textInput({
                                    value: c.headerName ?? '',
                                    onChange: headerName => model.updateColumnDraft({headerName}),
                                    commitOnChange: true,
                                    placeholder: specModel.getField(field)?.label ?? 'Default',
                                    width: 180
                                })
                            }),
                            configPanelItem({
                                label: 'Group',
                                item: select({
                                    value: model.columnDraftGroupId,
                                    onChange: v => model.setColumnDraftGroupId(v),
                                    options: model.groupOptions,
                                    enableFilter: false,
                                    width: 180
                                })
                            }),
                            configPanelItem({
                                label: 'Align',
                                item: select({
                                    value: c.align ?? null,
                                    onChange: align => model.updateColumnDraft({align}),
                                    options: ALIGN_OPTIONS,
                                    enableFilter: false,
                                    width: 180
                                })
                            }),
                            configPanelItem({
                                label: 'Pinned',
                                item: select({
                                    value: c.pinned ?? null,
                                    onChange: pinned => model.updateColumnDraft({pinned}),
                                    options: PINNED_OPTIONS,
                                    enableFilter: false,
                                    width: 180
                                })
                            }),
                            configPanelItem(
                                configPanelSwitch({
                                    label: 'Fill Available Width',
                                    value: !!c.flex,
                                    onChange: flex => model.updateColumnDraft({flex})
                                })
                            ),
                            configPanelItem({
                                label: 'Width',
                                omit: !!c.flex,
                                item: numberInput({
                                    value: c.width ?? null,
                                    onChange: width => model.updateColumnDraft({width}),
                                    placeholder: 'Default',
                                    min: 20,
                                    max: 1000,
                                    width: 180
                                })
                            }),
                            configPanelItem({
                                label: 'Total',
                                omit: !specModel.capabilities.totals,
                                item: select({
                                    value: c.aggregator ?? null,
                                    onChange: aggregator => model.updateColumnDraft({aggregator}),
                                    options: AGGREGATOR_OPTIONS,
                                    enableFilter: false,
                                    width: 180
                                })
                            }),
                            configPanelItem(
                                configPanelSwitch({
                                    label: 'Tooltip',
                                    value: !!c.tooltip,
                                    onChange: tooltip => model.updateColumnDraft({tooltip})
                                })
                            )
                        ]
                    }),
                    stackedPanel({
                        title: 'Format',
                        items: [
                            configPanelItem({
                                label: 'Type',
                                item: select({
                                    value: f.type,
                                    onChange: type => model.updateColumnDraft({format: type === 'text' ? null : {type}}),
                                    options: TYPE_OPTIONS,
                                    enableFilter: false,
                                    width: 180
                                })
                            }),
                            configPanelItem({
                                label: 'Style',
                                omit: !isNumber,
                                item: select({
                                    value: f.numberStyle ?? 'plain',
                                    onChange: numberStyle => model.updateFormatDraft({numberStyle}),
                                    options: NUMBER_STYLE_OPTIONS,
                                    enableFilter: false,
                                    width: 180
                                })
                            }),
                            configPanelItem({
                                label: 'Precision',
                                omit: !isNumber,
                                item: numberInput({
                                    value: typeof f.precision === 'number' ? f.precision : null,
                                    onChange: precision => model.updateFormatDraft({precision: isNil(precision) ? undefined : precision}),
                                    placeholder: 'Auto',
                                    min: 0,
                                    max: 12,
                                    width: 180
                                })
                            }),
                            configPanelItem({
                                label: 'Prefix / Suffix',
                                omit: !isNumber,
                                items: [
                                    textInput({
                                        value: f.prefix ?? '',
                                        onChange: prefix => model.updateFormatDraft({prefix: prefix || undefined}),
                                        commitOnChange: true,
                                        placeholder: 'e.g. $',
                                        width: 85
                                    }),
                                    textInput({
                                        value: f.suffix ?? '',
                                        onChange: suffix => model.updateFormatDraft({suffix: suffix || undefined}),
                                        commitOnChange: true,
                                        placeholder: 'e.g. m',
                                        width: 85,
                                        marginLeft: 10
                                    })
                                ]
                            }),
                            ...(isNumber
                                ? [
                                      ['currency', 'Currency ($)'],
                                      ['colorSpec', 'Color +/-'],
                                      ['ledger', 'Ledger (negatives in parens)'],
                                      ['withPlusSign', 'Plus Sign']
                                  ].map(([key, label]) =>
                                      configPanelItem({
                                          key,
                                          item: configPanelSwitch({
                                              label,
                                              value: !!f[key],
                                              onChange: v => model.updateFormatDraft({[key]: v || undefined})
                                          })
                                      })
                                  )
                                : []),
                            configPanelItem({
                                label: 'Date Style',
                                omit: !isDate,
                                item: select({
                                    value: f.dateStyle ?? 'date',
                                    onChange: dateStyle => model.updateFormatDraft({dateStyle}),
                                    options: DATE_STYLE_OPTIONS,
                                    enableFilter: false,
                                    width: 180
                                })
                            }),
                            configPanelItem({
                                label: 'Custom Pattern',
                                omit: !isDate,
                                item: textInput({
                                    value: f.dateFmt ?? '',
                                    onChange: dateFmt => model.updateFormatDraft({dateFmt: dateFmt || undefined}),
                                    commitOnChange: true,
                                    placeholder: 'e.g. YYYY, MMM YYYY',
                                    width: 180
                                })
                            })
                        ]
                    }),
                    styleRulesPanel({scope: 'cell', title: 'Cell Styles'})
                ],
                bbar: toolbar(
                    button({
                        text: 'Remove Column',
                        icon: Icon.delete(),
                        intent: 'danger',
                        minimal: true,
                        onClick: () => {
                            model.removeColumn(field);
                            model.closeColumnDialog();
                        }
                    }),
                    filler(),
                    button({text: 'Cancel', onClick: () => model.closeColumnDialog()}),
                    button({text: 'Save', intent: 'success', onClick: () => model.saveColumnDialog()})
                )
            })
        });
    }
});
